
import { Vue, Options } from "vue-class-component";
import { Ref } from "vue-property-decorator";
import Page from "@/components/molecules/Page.vue";
import ActionButton from "@/components/atoms/ActionButton.vue";
import Table from "@/components/molecules/Table.vue";
import ASGroupForm from "./ASGroupForm.vue";
import SendMailForm from "../../organisms/SendMailForm.vue";
import UIkit from "uikit";
import {
  AsApi,
  SystemApi,
  GetAsBroadcastResponse,
  AsBroadcastTypeEnum,
  GetEmployeeProfile,
  GetApplicantProfile,
  GetAsBroadcastStatus,
  GenderEnum,
  AsBroadcastStatusEnum
} from "../../../autogen";
import { InstructionLevel, SelectableProfile } from "../../../types";
import moment from "moment-timezone";
import DepartmentUserSelector from "../../organisms/DepartmentUserSelector.vue";
import Store from "../../../store";
import SearchField from "../../atoms/SearchField.vue";

type Participant = (GetEmployeeProfile | GetApplicantProfile) & GetAsBroadcastStatus & { selected?: boolean };

@Options({
  components: {
    Page,
    ActionButton,
    Table,
    ASGroupForm,
    SendMailForm,
    DepartmentUserSelector,
    SearchField
  }
})
export default class ASDetail extends Vue {
  selectedUsers: SelectableProfile[] = [];
  broadcast: (GetAsBroadcastResponse & { typeName: string; term: string }) | null = null;
  participants: Participant[] = [];
  filterColumns = ["name", "genderName", "email", "sendTask.status", "status"];
  header = [
    {
      key: "selected",
      width: 80,
      type: "selection",
      checked: false,
      disabled: (row: GetAsBroadcastStatus): boolean =>
        this.expired || ![AsBroadcastStatusEnum.None, AsBroadcastStatusEnum.Sent].some(r => r === row.sendTask!.status!)
    },
    { title: "", sortable: false, key: "avatar", width: 50, type: "avatar" },
    { title: "名前", sortable: true, key: "name", width: "expand" },
    { title: "性別", sortable: true, key: "genderName", width: 100 },
    { title: "メールアドレス", key: "email", width: "expand" },
    { title: "送信履歴", key: "formattedLastNotfiedAt", width: 150 },
    { title: "回答状況", key: "status", width: 150 },
    {
      title: (row: GetAsBroadcastStatus): string =>
        row.sendTask && row.sendTask.url && AsBroadcastStatusEnum.Expired !== row.sendTask!.status! ? "コピー" : "再発行",
      key: "copy",
      width: 110,
      type: "button",
      render: (row: GetAsBroadcastStatus): boolean =>
        !this.expired && ![AsBroadcastStatusEnum.Answering, AsBroadcastStatusEnum.Done].some(r => r === row.sendTask!.status!)
    },
    {
      title: "無効化",
      key: "disable",
      width: 110,
      type: "button",
      render: (row: GetAsBroadcastStatus): boolean =>
        !this.expired && [AsBroadcastStatusEnum.None, AsBroadcastStatusEnum.Sent].some(r => r === row.sendTask!.status!)
    }
  ];
  @Ref("editor") editor!: HTMLElement;
  @Ref("send-mail") sendMail!: HTMLElement;
  @Ref("modal") modal!: HTMLElement;
  editorFormKey = 0;
  sendMailFormKey = 0;
  departmentUserSelectorFormKey = 0;
  expired = false;
  currentOperation!: { row?: Participant; opCode: string };
  bcType = AsBroadcastTypeEnum;
  renewKey = 0;

  async loadData() {
    const response = await new AsApi().getAsBroadcast({
      asBroadcastId: this.$route.params["asBroadcastId"] as string
    });
    this.broadcast = Object.assign(
      {
        typeName: response.data.type === AsBroadcastTypeEnum.Employee ? "従業員" : "応募者",
        term: `${moment(response.data.startDateTime).format("YYYY年MM月DD日 HH時mm分")} ~ ${moment(
          response.data.endDateTime
        ).format("YYYY年MM月DD日 HH時mm分")}`
      },
      response.data
    );
    const participants =
      this.broadcast.type === AsBroadcastTypeEnum.Employee ? response.data.employees! : response.data.applicants!;
    this.participants = participants.map((participant: Participant) => {
      let status = "未回答";
      if (participant.sendTask && participant.sendTask.answeredDateTime) {
        status = `${moment(participant.sendTask.answeredDateTime).format("YYYY/MM/DD")}\n回答済`;
      } else if (participant.sendTask && participant.sendTask.status === AsBroadcastStatusEnum.Expired) {
        status = "期限切れ";
      }
      return Object.assign(
        {
          name: `${(participant.department ? participant.department.name : "") +
            "\n" +
            (participant.position ? participant.position.name : "")}\n${participant.lastName} ${participant.firstName}`,
          genderName:
            participant.gender === GenderEnum.Male ? "男性" : participant.gender === GenderEnum.Female ? "女性" : "未回答",
          avatar: participant.image ? participant.image : "",
          formattedLastNotfiedAt:
            participant.sendTask && participant.sendTask.emailDateTime
              ? moment(participant.sendTask.emailDateTime).format("YYYY/MM/DD") +
                "\n" +
                (participant.sendTask.emailSentErrorFlag ? "送信エラー" : "送信済")
              : null,
          status: status,
          selected: false
        },
        participant
      );
    });
    this.selectedUsers = this.participants.map(participant => ({
      id: this.broadcast!.type === AsBroadcastTypeEnum.Employee ? `EMP#${participant.id}#` : `APL#${participant.id}#`,
      firstName: participant.firstName,
      lastName: participant.lastName,
      email: "",
      image: participant.image ? participant.image : "",
      department: participant.department!,
      contractType: participant.contractType,
      disabled: participant.sendTask!.status !== AsBroadcastStatusEnum.None
    }));
    this.renewKey = Date.now();
  }
  async beforeMount() {
    await this.loadData();
    this.expired = moment(this.broadcast!.endDateTime).valueOf() < moment().valueOf();
  }
  async onConfirm() {
    if (this.currentOperation.opCode === "revoked") {
      await new AsApi().invalidateAsUrl({
        asSendTaskId: this.currentOperation.row!.sendTask!.asSendTaskId
      });
      this.currentOperation.row!.sendTask!.url = "";
      await this.loadData();
    } else if (this.currentOperation.opCode === "delete") {
      Store.getInstance().setIsLoading(true);
      const deleteAsSendTaskIds = this.participants
        .filter(p => p.selected)
        .map(function(value) {
          return value.sendTask!.asSendTaskId;
        });
      try {
        await new AsApi().deleteAsSendTasks({
          asBroadcastId: this.broadcast!.asBroadcastId,
          deleteAsSendTasksRequest: {
            asSendTaskIds: deleteAsSendTaskIds
          }
        });
        UIkit.modal(this.modal).hide();
        Store.getInstance().setInstructionContent(InstructionLevel.Info, "受検者を削除しました");
        this.loadData();
      } catch (err) {
        Store.getInstance().setInstructionContent(
          InstructionLevel.Alert,
          "エラーが発生しました。件数が多い場合は処理に多少時間がかかります。"
        );
      } finally {
        Store.getInstance().setIsLoading(false);
      }
      this.header[0].checked = false;
    }
  }
  showTooltip(el: HTMLElement) {
    el.setAttribute("uk-tooltip", "title: コピーしました");
    UIkit.tooltip(el).show();
    // @ts-ignore
    UIkit.util.once(document, "beforehide", ".uk-tooltip.uk-active", () => {
      el.removeAttribute("uk-tooltip");
    });
  }
  async onColumnClick(location: { row: number; col: string; el: HTMLElement }) {
    const row = this.participants[location.row];
    if (location.col === "copy") {
      if (row.sendTask && row.sendTask.url && row.sendTask!.status !== AsBroadcastStatusEnum.Expired) {
        navigator.clipboard.writeText(row.sendTask.url);
        this.showTooltip(location.el);
      } else {
        const response = await new AsApi().issueAsUrl({
          asSendTaskId: row.sendTask!.asSendTaskId
        });
        this.participants[location.row]!.sendTask!.url = response.data.url;
        await this.loadData();

        Store.getInstance().setInstructionContent(InstructionLevel.Info, "URL再発行が完了しました");
      }
    } else if (location.col === "disable") {
      Store.getInstance().setInstructionContent(
        InstructionLevel.Confirm,
        `現在発行されているURLが無効となり、受検できなくなります。よろしいですか？`
      );
      this.currentOperation = { row, opCode: "revoked" };
    }
  }
  onEditButtonClick() {
    this.editorFormKey = Date.now();
    UIkit.modal(this.editor).show();
  }
  async onURLDownloadButtonClick() {
    const encoding = require("encoding-japanese");
    const response = await new SystemApi().exportAsUrlCsv({
      asBroadcastId: this.broadcast!.asBroadcastId
    });

    const str_array = encoding.stringToCode(response.data);
    const sjis_array = encoding.convert(str_array, "SJIS", "UNICODE");
    const uint8_array = new Uint8Array(sjis_array);

    const url = window.URL.createObjectURL(new Blob([uint8_array], { type: "text/csv;charset=Shift_JIS" }));

    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.setAttribute("download", `受検URL一覧_${this.broadcast!.name}_${new Date().toISOString()}.csv`);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }
  async onGroupFormSubmitted() {
    UIkit.modal(this.editor).hide();
    await this.loadData();
  }
  onEmailSendButtonClick() {
    this.currentOperation = { opCode: "send" };
    if (this.participants.filter(p => p.selected).length) {
      this.sendMailFormKey = Date.now();
      UIkit.modal(this.sendMail).show();
    } else {
      Store.getInstance().setInstructionContent(
        InstructionLevel.Alert,
        `メール送信をする対象者が選択されておりません。\nチェックボックスより、メール送信対象者を選択してからURLメール送信ボタンを押してください。`
      );
    }
  }
  async onSendMailSubmitted(errIds: string[]) {
    UIkit.modal(this.sendMail).hide();
    if (errIds && errIds.length && this.participants) {
      const errUsers = this.participants
        .filter(p => errIds.includes(p.sendTask!.asSendTaskId))
        .map(p => p.lastName.concat(" ", p.firstName, "さん"));
      alert(
        `以下の受検者はメールアドレスが設定されていないため、メールアドレスが設定されている受検者にのみメールを送信しました。\n${errUsers.join(
          "\n"
        )}`
      );
    }
    this.header[0].checked = false;
    await this.loadData();
  }
  onSelectionChange(row: { index: number; value: boolean }) {
    this.participants[row.index].selected = row.value;
  }
  onSelectionAllChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (this.participants) {
      this.participants.forEach(row => {
        const disable =
          this.expired || ![AsBroadcastStatusEnum.None, AsBroadcastStatusEnum.Sent].some(r => r === row.sendTask!.status!);
        if (!disable) {
          row.selected = target.checked;
        }
      });
    }
  }
  onAddButtonClick() {
    this.departmentUserSelectorFormKey = Date.now();
    UIkit.modal(this.modal).show();
  }

  async onDeleteButtonClick() {
    if (this.participants.filter(p => p.selected).length) {
      this.currentOperation = { opCode: "delete" };
      Store.getInstance().setInstructionContent(InstructionLevel.Confirm, `選択した受検者を削除します。よろしいでしょうか？`);
    } else {
      this.currentOperation = { opCode: "none" };
      Store.getInstance().setInstructionContent(
        InstructionLevel.Alert,
        `削除する対象者が選択されておりません。\nチェックボックスより、削除対象者を選択してから削除ボタンを押してください。`
      );
    }
  }
  onTargetUserSelectionChange(selectedUsers: SelectableProfile[]) {
    this.selectedUsers = selectedUsers;
  }
  async onSelectionDoneButtonClick() {
    await UIkit.modal(this.modal).hide();
    Store.getInstance().setIsLoading(true);
    let employeeIds: string[] = [];
    let applicantIds: string[] = [];

    if (this.broadcast!.type === AsBroadcastTypeEnum.Employee) {
      employeeIds = this.selectedUsers.map(user => user.id.replace(/(APL|EMP)/g, "").replace(/#/g, ""));
    } else if (this.broadcast!.type === AsBroadcastTypeEnum.Applicant) {
      applicantIds = this.selectedUsers.map(user => user.id.replace(/(APL|EMP)/g, "").replace(/#/g, ""));
    }
    const peopleCount = employeeIds.length + applicantIds.length;
    try {
      await new AsApi().registerAsSendtask({
        asBroadcastId: this.broadcast!.asBroadcastId,
        registerAsSendTaskRequest: {
          employeeIds,
          applicantIds
        }
      });
    } catch (err) {
      let errMessage = "エラーが発生しました";
      if (err.response && err.response.data) {
        errMessage = err.response.data.detail[0];
      }
      Store.getInstance().setInstructionContent(InstructionLevel.Danger, errMessage);
    } finally {
      setTimeout(
        () => {
          Store.getInstance().setIsLoading(false);
        },
        Number(peopleCount) != 0 ? 0 : 200
      );
    }
    this.loadData();
  }
  async onSearch(searchResult: [] | null) {
    if (searchResult) {
      this.participants = searchResult;
    } else {
      await this.loadData();
    }
  }
  async onGroupFromDeleted() {
    UIkit.modal(this.editor).hide();
    this.$router.push(`/clients/${this.$route.params["clientId"]}/as/group`);
  }
  allSelectionCallback(tableData: { [key: string]: any }[], checked: boolean) {
    if (tableData) {
      tableData.forEach(row => {
        console.log("run callback.");
        const disable =
          this.expired || ![AsBroadcastStatusEnum.None, AsBroadcastStatusEnum.Sent].some(r => r === row.sendTask!.status!);
        if (!disable) {
          row.selected = checked;
        }
      });
    }
  }
}
